import { useDarkMode } from "@/hooks/useDarkMode";
import React from "react";
import { IoCheckmarkCircleOutline } from "react-icons/io5";
import { CiWarning } from "react-icons/ci";
import { PiInfo } from "react-icons/pi";
import { XIcon } from "lucide-react";

interface CustomToastProps {
  message: string;
  type?: "error" | "success" | "normal" | "warning";
  // When set, renders a two-line title + description block instead of `message`.
  title?: string;
  description?: string;
  onClose?: () => void;
}

const CustomToast: React.FC<CustomToastProps> = ({
  message,
  type = "normal",
  title,
  description,
  onClose,
}) => {
  let icon;
  let backgroundColor;

  const isDark = useDarkMode();

  switch (type) {
    case "success":
      backgroundColor = "#10B981"; // Green color for success
      icon = (
        <IoCheckmarkCircleOutline
          size={24}
          color={isDark ? "#6EE7B7" : backgroundColor}
          aria-hidden="true"
        />
      );
      break;
    case "error":
      backgroundColor = "#F87171"; // Red color for error
      icon = <CiWarning size={24} color={backgroundColor} aria-hidden="true" />;
      break;
    case "warning":
      backgroundColor = "#F59E0B"; // Amber — partial success / non-blocking
      icon = <CiWarning size={24} color={backgroundColor} aria-hidden="true" />;
      break;
    default:
      backgroundColor = "#352FC3"; // Blue color for normal
      icon = <PiInfo size={24} color={isDark ? "#A39FE8" : backgroundColor} aria-hidden="true" />;
      if (isDark) {
        backgroundColor = "#A39FE8";
      }
      break;
  }

  const hasTitle = !!title;
  return (
    <div
      role="status"
      aria-live={type === "error" ? "assertive" : "polite"}
      aria-atomic="true"
      style={{
        borderTopColor: backgroundColor,
      }}
      className="bg-bg-brand-secondary-default rounded-[10px] border-t-[8px] flex flex-col"
    >
      <div className="flex-1 py-[12px] flex flex-row">
        <div className="px-[12px]">{icon}</div>
        <div className="flex-1 justify-center pr-[16px]">
          {hasTitle ? (
            <>
              <div className="label-m-semibold text-fg-neutral-strong">
                {title}
              </div>
              {description ? (
                <div className="label-s-regular text-fg-neutral-subtle mt-[2px]">
                  {description}
                </div>
              ) : null}
            </>
          ) : (
            <div className="label-m-medium text-fg-neutral-strong">
              {message}
            </div>
          )}
        </div>
        {hasTitle && onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Dismiss notification"
            className="px-[12px] text-fg-neutral-subtle focus-blue-ring"
          >
            <XIcon size={18} aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default CustomToast;
